import { TrackJS } from 'trackjs';

import Dropzone from '@component/dropzone';
import Browser from '@wetransfer/concorde-browser';
import Router from '@module/router';
import Store from '@app/store';
import Actions from '@app/action';

const firstVisit = new Date();

const storeEvents = {
  transfer: [
    { oldState: 'init', newState: 'started', name: 'uploadstarted' },
    { oldState: 'started', newState: 'finished', name: 'uploadfinished' },
    { oldState: 'downloading', newState: 'finished', name: 'downloadstarted' },
  ],
  terms: [{ oldState: false, newState: true, name: 'termsaccepted' }],
};

export default class HTML5Api {
  constructor(wallpaper) {
    this.wallpaper = wallpaper;
    this.remoteHost = false;
    this.vast = false;
    this.panelOpen = false;
    this.getStoreUpdates();
    this.lastTransferState = Store.getState().transfer.state;
    this.lastTermsState = Store.getState().session.tandc;
  }

  getStoreUpdates() {
    Store.subscribe(() => {
      const {
        transfer: { state },
        session: { tandc },
      } = Store.getState();
      if (this.lastTransferState === state && this.lastTermsState === tandc) {
        return;
      }

      const transferEvent = storeEvents.transfer.find(
        ({ oldState, newState }) =>
          this.lastTransferState === oldState && state === newState
      );
      const termsEvent = storeEvents.terms.find(
        ({ oldState, newState }) =>
          this.lastTermsState === oldState && tandc === newState
      );
      const event = transferEvent || termsEvent;
      if (event) {
        this.sendMessage('event', { name: event.name, data: {} });
      }

      this.lastTransferState = state;
      this.lastTermsState = tandc;
    });
  }

  setWallpaper(media) {
    // Set the 'remote host' (wallpaper hostname)
    const remoteHost = media.fullPath.match(/(https?:\/\/[^/]+)(?=\/?)/i);

    if (!remoteHost) {
      return false;
    }

    this.remoteHost = remoteHost[0];

    // Bind event listener (the simple way)
    this.wallpaper.frameNode.contentWindow.onmessage = (e) =>
      this.parseMessage(e);

    // Determine panel state
    const { route } = Store.getState();
    this.panelOpen = route.type !== 'transfer';
  }

  setPanelState(isOpen) {
    if (isOpen === this.panelOpen) {
      return;
    }
    this.panelOpen = isOpen;
    this.sendMessage('panelStateChange', isOpen);
  }

  startWallpaper() {
    const state = this.getCurrentState();
    state['_click'] = this.wallpaper.currentMedia.href;
    this.sendMessage('start', state);
  }

  getCurrentState() {
    const { route, session } = Store.getState();

    let ads_viewed = 0;
    if ('api' in this.wallpaper.provider) {
      ads_viewed = this.wallpaper.provider.api.currentPosition - 1;
    }

    // Get the position for Transfer and Navigation
    const transfer = document.querySelector('.transfer');
    const transferRect = transfer.getBoundingClientRect();
    const nav = document.querySelector('.nav');
    const navRect = nav.getBoundingClientRect();

    // If there are analytic scripts we'll need to update the variables.
    let _analytics = [];
    if (this.wallpaper.currentMedia.meta.analytics) {
      _analytics = this.wallpaper.currentMedia.meta.analytics.map((script) => {
        // replace {placement.id} with the value of auid_marker_counter
        return script.replace(/\s*\{placement\.id\}\s*/g, ads_viewed);
      });
    }

    return {
      _analytics,
      ads_viewed,
      geo: session.region,
      state: route.path.match(/download/) ? 'download' : 'upload',
      panel: this.panelOpen,
      language: session.language,
      remaining: this.wallpaper.timer ? this.wallpaper.timer.remainingTime : 30,
      time_on_site: (new Date() - firstVisit) / 1000,
      user_agent: Browser.identity, // {platform, browser, version}
      position: {
        x: Math.round(transferRect.left),
        y: Math.round(transferRect.top),
        width: Math.round(transferRect.width),
        height: Math.round(transferRect.height),
      },
      nav_position: {
        x: Math.round(navRect.left),
        y: Math.round(navRect.top),
        width: Math.round(navRect.width),
        height: Math.round(navRect.height),
      },
    };
  }

  reset() {
    this.vast = false;
  }

  setVast(vast = false) {
    this.vast = vast;
  }

  parseMessage({ origin, data }) {
    if (!origin || !data) {
      return;
    }

    let payload = {};
    // Try to parse JSON.
    try {
      payload = data.raw ? data : JSON.parse(data);
    } catch (e) {
      return false;
    }

    // Check if command exists.
    if (!payload.command) {
      return false;
    }

    const method = 'api_' + payload.command;
    if (!(method in this)) {
      TrackJS.addMetadata('command', payload.command);
      TrackJS.track(new Error('InvalidCommand'));
      return false;
    }

    // It should work :)
    this[method](...payload.data);
  }

  sendMessage(action, ...params) {
    if (!this.wallpaper || !this.wallpaper.currentMedia || !this.remoteHost) {
      return false;
    }

    const {
      currentMedia: { node },
    } = this.wallpaper;
    if (!node || !node.contentWindow) {
      return false;
    }

    // Prepare the payload
    const payload = JSON.stringify({
      command: action,
      data: params,
    });

    // Send the message
    node.contentWindow.postMessage(payload, this.remoteHost);
  }

  //
  // BEHOLD, FOR HERE ARE THE EXTERNAL API COMMANDS:
  //
  api_toggleDragOver() {
    Dropzone.show();
  }

  api_showDropZone() {
    Dropzone.show();
  }

  api_hideDropZone() {
    Dropzone.hide();
  }

  api_addFiles(...files) {
    if (Dropzone.target) {
      /**
       * Because of an issue with Drag & Drop in Chrome, we are specifying that we are
       * adding files from an iFrame. So we can handle this case gracefully in the Dropzone
       * target. Currently, this happens only in Chrome but in the future this might happen
       * in other browsers as well.
       */
      Dropzone.target.addFiles(files, { isSentFromIFrame: true });
    }
    Dropzone.hide();
  }

  api_show() {
    Store.dispatch(Actions.Layout.showUI());
  }

  api_hide() {
    Store.dispatch(Actions.Layout.hideUI());
  }

  api_showUpload() {
    if (Store.getState().transfer.state !== 'finished') {
      return;
    }

    Store.dispatch(Actions.Transfer.create());
    Router.navigateTo('/');
  }

  api_pauseTimer() {
    if (!this.wallpaper.timer) {
      return;
    }
    this.wallpaper.timer.pause();
  }

  api_resumeTimer() {
    if (!this.wallpaper.timer) {
      return;
    }
    this.wallpaper.timer.resume();
  }

  api_resetTimer() {
    if (!this.wallpaper.timer) {
      return;
    }
    this.wallpaper.timer.stop();
    this.wallpaper.timer.start();
  }

  api_openPanel(path, trk = '') {
    // Whitelisted paths.
    if (!path.match(/^\/(plus|sign-in|about|apps)/i)) {
      return;
    }

    // Do we have tracking information?
    const params = {};
    if (trk) {
      params['search'] = 'trk=' + trk;
    }

    Router.navigateTo(path, false, params);
  }

  api_change() {
    // changeAdvert
    if (this.wallpaper.timer) {
      this.wallpaper.timer.stop();
    }

    this.wallpaper.rotateMedia();
  }

  api_forceChange() {
    if (this.wallpaper.timer) {
      this.wallpaper.timer.stop();
    }

    this.wallpaper.determineWallpaperProvider();
  }

  api_getState(requester) {
    const state = this.getCurrentState();
    state['requester'] = requester;
    this.sendMessage('update', state);
  }

  api_vast(name, filename) {
    if (!this.vast) {
      return;
    }
    this.vast.track(name, filename);
  }

  api_setWallpaperUIStyle(styles = {}) {
    Store.dispatch(Actions.Layout.setWallpaperUIStyle(styles));
  }

  api_setAppTheme(styles) {
    document.body.className = `${document.body.className} ${styles.className}`;
    const link = document.createElement('link');
    link.setAttribute('id', `wallpaper-theme-${styles.className}`);
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('type', 'text/css');
    link.setAttribute('href', styles.href);
    document.getElementsByTagName('head')[0].appendChild(link);
  }
}
